// One integration: editable config, vendor-reported quota/balance, our metered
// usage, its alarms, the registry's change history and the config audit trail
// (ff-api#1232, phases 1-2).
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Integration } from '@/client/types/integrations';
import {
  useIntegrationAuditQuery,
  useIntegrationQuery,
  useProbeIntegrationMutation,
} from '@/client/api/integrations/integrations.query';
import ConfigEditor, { EditConfigButton } from './ConfigEditor';
import EnableControl from './EnableControl';
import TurnstileAnalytics from './TurnstileAnalytics';
import {
  AccessDenied,
  ErrorSampleRow,
  INTEGRATIONS_BASE,
  PageShell,
  SeverityBadge,
  StatusBadge,
  UsageBar,
  UtilizationTable,
  fmtDate,
  fmtErrorRate,
  fmtLatency,
  fmtNum,
  fmtPct,
  fmtUsd,
  usagePct,
  WindowPicker,
} from './integrationsUi';

const Card: React.FC<{
  title: string;
  children: React.ReactNode;
  note?: string;
  action?: React.ReactNode;
}> = ({ title, children, note, action }) => (
  <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
    <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <div className="flex items-baseline gap-3">
        {note && <span className="text-xs text-gray-400">{note}</span>}
        {action}
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <div className="truncate text-sm text-gray-900">{children}</div>
  </div>
);

const unset = <span className="text-amber-600">not set</span>;

/** Audit values are whatever was stored: a string, a number or a cost object. */
const fmtValue = (v: unknown): string => {
  if (v == null || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};

/** Structured cost from the overlay, or the legacy free-text YAML value. */
const formatCost = (cost: Integration['cost']): React.ReactNode => {
  if (!cost) return unset;
  if (typeof cost === 'string') return cost;
  const currency = cost.currency ?? 'USD';
  if (cost.model === 'subscription' && cost.monthly_amount != null) {
    return `${cost.monthly_amount} ${currency}/mo`;
  }
  if (cost.model === 'per_call' && cost.unit_amount != null) {
    return `${cost.unit_amount} ${currency}/call`;
  }
  return cost.model;
};

/** Calls per day with the error share stacked on top; CSS bars, no chart library. */
const TrafficSparkline: React.FC<{ series?: Record<string, { calls: number; errors: number }> }> = ({
  series,
}) => {
  const days = Object.entries(series ?? {}).sort(([a], [b]) => a.localeCompare(b));
  if (days.length === 0) return null;
  const peak = Math.max(...days.map(([, v]) => v.calls), 1);
  return (
    <div className="mt-4">
      <div className="flex h-16 items-end gap-0.5">
        {days.map(([day, v]) => {
          const height = Math.max(2, (v.calls / peak) * 100);
          const errorShare = v.calls ? (v.errors / v.calls) * 100 : 0;
          return (
            <div
              key={day}
              title={`${day}: ${v.calls} calls, ${v.errors} errors`}
              className="flex-1 overflow-hidden rounded-sm bg-blue-200"
              style={{ height: `${height}%` }}
            >
              <div className="w-full bg-red-400" style={{ height: `${errorShare}%` }} />
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-400">
        {days[0][0]} — {days[days.length - 1][0]}, red is the error share
      </p>
    </div>
  );
};

const IntegrationDetailPage: React.FC = () => {
  const { name = '' } = useParams<{ name: string }>();
  useBreadcrumbs([{ label: 'Integrations', href: INTEGRATIONS_BASE }, { label: name }]);

  const viewPerm = useHasPermission('integration.view');
  const managePerm = useHasPermission('integration.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState(30);
  const probe = useProbeIntegrationMutation();
  const detailQ = useIntegrationQuery(name, { days }, canView);
  const auditQ = useIntegrationAuditQuery(name, canView);

  if (viewPerm.isLoading) {
    return (
      <PageShell title={name} description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title={name} description="">
        <AccessDenied />
      </PageShell>
    );
  }

  const row = detailQ.data?.integration;
  const computed = row?.computed;
  const observed = row?.observed;
  const usage = row?.usage;
  const selfReported = observed?.self_reported;
  const { pct, label } = row ? usagePct(row) : { pct: null, label: '' };

  return (
    <PageShell
      title={name}
      description={row?.purpose ?? row?.notes ?? 'Third-party integration'}
      actions={
        <div className="flex items-center gap-2">
          <WindowPicker days={days} onChange={setDays} />
          <Button variant="outline" size="sm" asChild>
            <Link to={INTEGRATIONS_BASE}>
              <ArrowLeft className="mr-2 h-3.5 w-3.5" />
              All integrations
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => detailQ.refetch()}
            disabled={detailQ.isFetching}
          >
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${detailQ.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {detailQ.isLoading && (
        <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />
        </div>
      )}

      {detailQ.isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(detailQ.error)}
        </div>
      )}

      {row && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <SeverityBadge severity={computed?.health_severity} />
            <StatusBadge status={row.status} />
            {computed?.audited === false && (
              <span className="text-xs text-amber-600">
                unaudited — {computed.unfilled_fields?.length ?? 0} lifecycle fields empty
              </span>
            )}
            {(computed?.reasons?.length ?? 0) > 0 && (
              <span className="text-xs text-gray-500">{computed?.reasons?.join('; ')}</span>
            )}
          </div>

          {name === 'turnstile' && (
            <Card title="Security checks" note="live from Cloudflare">
              <TurnstileAnalytics days={days} />
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card
              title="Configuration"
              note={row.repo}
              action={
                canManage && !editing ? <EditConfigButton onClick={() => setEditing(true)} /> : null
              }
            >
              {editing ? (
                <ConfigEditor row={row} onDone={() => setEditing(false)} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                <Field label="Provider">{row.provider ?? '—'}</Field>
                <Field label="Category">{row.category ?? '—'}</Field>
                <Field label="Owner">{row.owner ?? unset}</Field>
                <Field label="Plan">{row.plan ?? unset}</Field>
                <Field label="Cost">{formatCost(row.cost)}</Field>
                <Field label="Limits">{row.limits ?? unset}</Field>
                <Field label="Module">{row.module ?? '—'}</Field>
                <Field label="Kill switch">
                  {row.enable_flag ? (
                    <code className="text-xs">{row.enable_flag}</code>
                  ) : (
                    <span className="text-gray-400">none</span>
                  )}
                </Field>
                <Field label="Credential env vars">
                  {row.keys?.length ? (
                    <code className="text-xs">{row.keys.join(', ')}</code>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="Last verified">{row.last_verified ?? unset}</Field>
                <Field label="Key expires">
                  {computed?.expiry_on ?? row.key_expires ?? unset}
                  {computed?.days_to_expiry != null && ` (${computed.days_to_expiry}d)`}
                </Field>
                <Field label="Renewal">
                  {computed?.renewal_on ?? row.renewal_date ?? unset}
                  {computed?.days_to_renewal != null && ` (${computed.days_to_renewal}d)`}
                </Field>
              </div>
              <p className="mt-4 text-xs text-gray-400">
                Code-derived fields come from config/integrations.yaml. Lifecycle and cost fields
                are set here and stored as an overlay
                {row.overridden_fields?.length
                  ? ` — ${row.overridden_fields.join(', ')} set by an admin.`
                  : '.'}
              </p>
                </>
              )}
            </Card>

            <Card title="Quotas and validity" note={row.activity?.reason}>
              <UtilizationTable rows={row.utilization} />
            </Card>

            <Card
              title="Usage this month"
              note={detailQ.data?.month ? `month ${detailQ.data.month}` : undefined}
            >
              <div className="grid grid-cols-2 gap-4">
                <Field label="Vendor calls">{fmtNum(usage?.vendor_calls)}</Field>
                <Field label="Cache hits">{fmtNum(usage?.cache_hits)}</Field>
                <Field label="Estimated cost">
                  {usage?.est_cost ? fmtUsd(usage.est_cost) : '—'}
                </Field>
                <Field label="Last call">{fmtDate(usage?.last_at)}</Field>
                <Field label="Our monthly ceiling">{fmtNum(selfReported?.ceiling)}</Field>
                <Field label="Against ceiling">{fmtPct(selfReported?.ceiling_pct)}</Field>
              </div>
              <div className="mt-4">
                <UsageBar pct={pct} label={label} />
              </div>
              {!usage && (
                <p className="mt-3 text-xs text-gray-400">
                  No billed calls recorded for this vendor this month. Calls and errors for
                  every vendor appear under "Calls and errors" — billed usage is only counted
                  for the vendors that meter it.
                </p>
              )}
            </Card>

            <Card title="Service state" note="applies to this environment">
              <EnableControl row={row} canManage={canManage} />
            </Card>

            <Card
              title="Calls and errors"
              note={`last ${row.traffic?.days ?? detailQ.data?.traffic_days ?? 30} days`}
            >
              {row.traffic?.calls ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <Field label="Calls">{fmtNum(row.traffic.calls)}</Field>
                    <Field label="Errors">{fmtNum(row.traffic.errors)}</Field>
                    <Field label="Error rate">
                      <span
                        className={
                          (row.traffic.error_rate ?? 0) > 0.05 ? 'font-medium text-red-600' : ''
                        }
                      >
                        {fmtErrorRate(row.traffic.error_rate)}
                      </span>
                    </Field>
                    <Field label="Avg latency">{fmtLatency(row.traffic.avg_latency_ms)}</Field>
                    <Field label="Slowest">{fmtLatency(row.traffic.latency_ms_max)}</Field>
                    <Field label="Credentials rejected">
                      {row.traffic.auth_failed ? (
                        <span className="text-red-600">{fmtNum(row.traffic.auth_failed)}</span>
                      ) : (
                        '—'
                      )}
                    </Field>
                    <Field label="Rate limited">{fmtNum(row.traffic.rate_limited ?? 0)}</Field>
                    <Field label="Timeouts">{fmtNum(row.traffic.err_timeout ?? 0)}</Field>
                    <Field label="Circuit trips">
                      {row.traffic.circuit_trips ? (
                        <span className="text-amber-600">{fmtNum(row.traffic.circuit_trips)}</span>
                      ) : (
                        '—'
                      )}
                    </Field>
                  </div>
                  <TrafficSparkline series={row.traffic.series} />
                </>
              ) : (
                <p className="text-sm text-gray-400">
                  No outbound calls recorded in this window.
                </p>
              )}
            </Card>

            <Card
              title="Vendor-reported"
              note={observed?.checked_at ? `checked ${fmtDate(observed.checked_at)}` : 'no probe'}
              action={
                canManage ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => probe.mutate(name)}
                    disabled={probe.isPending}
                  >
                    {probe.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Stethoscope className="mr-2 h-3.5 w-3.5" />
                    )}
                    Check now
                  </Button>
                ) : null
              }
            >
              {probe.isSuccess && (
                <p
                  className={`mb-3 rounded-lg px-3 py-2 text-sm ${
                    probe.data?.probed?.auth_failed
                      ? 'bg-red-50 text-red-700'
                      : probe.data?.probed?.ok
                        ? 'bg-green-50 text-green-700'
                        : 'bg-amber-50 text-amber-800'
                  }`}
                >
                  {probe.data?.probed?.auth_failed
                    ? 'The vendor refused this credential — the key is dead or revoked.'
                    : probe.data?.probed?.ok
                      ? `Answered in ${Math.round(probe.data.probed.latency_ms ?? 0)}ms. ${probe.data.probed.detail ?? ''}`
                      : `No usable answer: ${probe.data?.probed?.error ?? 'this vendor exposes no check'}`}
                </p>
              )}
              {probe.isError && (
                <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {getApiErrorMessage(probe.error)}
                </p>
              )}
              {observed ? (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Probe">
                    {observed.ok ? 'ok' : observed.error ? `failed: ${observed.error}` : '—'}
                  </Field>
                  <Field label="Severity">
                    <SeverityBadge severity={observed.severity} />
                  </Field>
                  <Field label="Balance">
                    {observed.balance != null
                      ? `${observed.balance} ${observed.currency ?? ''}`.trim()
                      : '—'}
                  </Field>
                  <Field label="Used / limit">
                    {observed.used != null || observed.limit != null
                      ? `${fmtNum(observed.used)} / ${fmtNum(observed.limit)}`
                      : '—'}
                  </Field>
                  <Field label="Plan">{observed.plan ?? '—'}</Field>
                  <Field label="Condition">{observed.condition ?? '—'}</Field>
                  {observed.auth_failed && (
                    <Field label="Credentials">
                      <span className="text-red-600">rejected by vendor</span>
                    </Field>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  This vendor exposes no balance or quota endpoint, or the hourly pull has not run.
                </p>
              )}
            </Card>

            <Card
              title="What actually failed"
              note={`${detailQ.data?.errors?.length ?? 0} recorded`}
            >
              {(detailQ.data?.errors?.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-400">
                  No failures recorded for {name} in this window.
                </p>
              ) : (
                <>
                  <ul className="max-h-80 overflow-y-auto">
                    {(detailQ.data?.errors ?? []).map((sample, i) => (
                      <ErrorSampleRow key={`${sample.ts}-${i}`} sample={sample} />
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-gray-400">
                    Credentials are stripped before storage and the query string is dropped.
                  </p>
                </>
              )}
            </Card>

            <Card title="Alarms" note={`${detailQ.data?.alarms.length ?? 0} recorded`}>
              {(detailQ.data?.alarms.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-400">No alarms raised for this integration.</p>
              ) : (
                <ul className="space-y-3">
                  {detailQ.data?.alarms.map((alarm) => (
                    <li key={alarm._id} className="flex items-start gap-3">
                      <SeverityBadge severity={alarm.severity} />
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-800">{alarm.detail ?? alarm.condition}</p>
                        <p className="text-xs text-gray-400">
                          {alarm.status} · last seen {fmtDate(alarm.last_seen)}
                          {alarm.ack ? ` · acked by ${alarm.ack.by ?? '—'}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card
            title="Config edits"
            note={`${auditQ.data?.count ?? 0} recorded`}
          >
            {(auditQ.data?.audit.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-400">
                Nobody has changed this integration&apos;s config from the admin yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                      <th className="px-2 py-2 font-medium">When</th>
                      <th className="px-2 py-2 font-medium">Field</th>
                      <th className="px-2 py-2 font-medium">From</th>
                      <th className="px-2 py-2 font-medium">To</th>
                      <th className="px-2 py-2 font-medium">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditQ.data?.audit.map((rowAudit, i) => (
                      <tr
                        key={`${rowAudit.at ?? i}-${rowAudit.field ?? i}`}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="px-2 py-2 text-gray-500">{fmtDate(rowAudit.at)}</td>
                        <td className="px-2 py-2 font-medium text-gray-800">{rowAudit.field}</td>
                        <td className="px-2 py-2 text-gray-500">{fmtValue(rowAudit.old)}</td>
                        <td className="px-2 py-2 text-gray-800">{fmtValue(rowAudit.new)}</td>
                        <td className="px-2 py-2 text-gray-500">{rowAudit.by ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="History" note="appended when status or severity changes">
            {(detailQ.data?.history.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-400">No recorded changes yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                      <th className="px-2 py-2 font-medium">When</th>
                      <th className="px-2 py-2 font-medium">Health</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                      <th className="px-2 py-2 font-medium">Reasons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailQ.data?.history.map((h, i) => (
                      <tr key={`${h.ts ?? i}`} className="border-b border-gray-50 last:border-0">
                        <td className="px-2 py-2 text-gray-500">{fmtDate(h.ts)}</td>
                        <td className="px-2 py-2">
                          <SeverityBadge severity={h.health_severity} />
                        </td>
                        <td className="px-2 py-2 text-gray-700">{h.status ?? '—'}</td>
                        <td className="px-2 py-2 text-gray-600">
                          {Array.isArray(h.reasons) && h.reasons.length ? h.reasons.join('; ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </PageShell>
  );
};

export default IntegrationDetailPage;
